/**
 * Create Credit Note Page
 *
 * Dedicated page with 3-step wizard for creating credit notes:
 * 1. Select reference code (reason)
 * 2. Select line items to credit
 * 3. Preview and confirm
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ChevronLeft, ChevronRight, Check, Minus, ArrowLeft, Loader2 } from 'lucide-react'

import api from '@/lib/api'
import { useCreateCreditNote } from '@/hooks/useBills'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/components/ui/toaster'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BillDetail,
  BillLine,
  ReferenceCode,
  REFERENCE_CODE_LABELS,
  CREDIT_NOTE_REFERENCE_CODES,
  CreditNoteLineRequest,
} from '@/types'

// Lo que pagó el cliente por unidad: el precio de la línea menos su descuento.
// Acreditar el precio bruto devolvía de más en las líneas con descuento.
const netUnitPrice = (line: BillLine) =>
  Math.round(line.unit_price * (1 - (line.discount_percent || 0) / 100) * 100000) / 100000

interface SelectedLine {
  lineId: number
  line: BillLine
  quantity: number
  unitPrice: number
}

export function CreateCreditNotePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const createCreditNoteMutation = useCreateCreditNote()

  // Fetch bill data
  const { data: bill, isLoading } = useQuery({
    queryKey: ['bill', id],
    queryFn: async () => {
      const response = await api.get(`/bills/${id}`)
      return response.data as BillDetail
    },
    enabled: !!id,
  })

  // Wizard state
  const [step, setStep] = useState(1)

  // Step 1: Reference code and reason
  const [referenceCode, setReferenceCode] = useState<ReferenceCode | ''>('')
  const [reason, setReason] = useState('')

  // Step 2: Selected lines
  const [selectedLines, setSelectedLines] = useState<Map<number, SelectedLine>>(new Map())

  // Check if this is a full cancellation (anulación)
  const isFullCancellation = referenceCode === ReferenceCode.ANULA_DOCUMENTO

  // Calculate totals
  const calculateTotal = () => {
    let subtotal = 0
    let tax = 0
    selectedLines.forEach((selected) => {
      const lineSubtotal = selected.quantity * selected.unitPrice
      subtotal += lineSubtotal
      const lineTax = lineSubtotal * ((selected.line.tax_rate ?? 13) / 100)
      tax += lineTax
    })
    return { subtotal, tax, total: subtotal + tax }
  }

  const totals = calculateTotal()

  // Handlers for line selection
  const handleToggleLine = (line: BillLine, checked: boolean) => {
    const newSelected = new Map(selectedLines)
    if (checked) {
      newSelected.set(line.id, {
        lineId: line.id,
        line,
        quantity: line.quantity,
        unitPrice: netUnitPrice(line),
      })
    } else {
      newSelected.delete(line.id)
    }
    setSelectedLines(newSelected)
  }

  const handleSelectAll = (billData?: BillDetail) => {
    const targetBill = billData || bill
    if (!targetBill) return
    const newSelected = new Map<number, SelectedLine>()
    targetBill.lines.forEach((line) => {
      newSelected.set(line.id, {
        lineId: line.id,
        line,
        quantity: line.quantity,
        unitPrice: netUnitPrice(line),
      })
    })
    setSelectedLines(newSelected)
  }

  // Handle reference code change - auto-select all items for cancellation
  const handleReferenceCodeChange = (code: ReferenceCode) => {
    setReferenceCode(code)
    if (code === ReferenceCode.ANULA_DOCUMENTO && bill) {
      // For cancellation, auto-select all items
      handleSelectAll(bill)
    } else {
      // For return, clear selection so user can choose
      setSelectedLines(new Map())
    }
  }

  const handleDeselectAll = () => {
    setSelectedLines(new Map())
  }

  const handleQuantityChange = (lineId: number, quantity: number) => {
    const selected = selectedLines.get(lineId)
    if (selected) {
      const maxQty = selected.line.quantity
      const validQty = Math.min(Math.max(1, quantity), maxQty)
      setSelectedLines(new Map(selectedLines.set(lineId, { ...selected, quantity: validQty })))
    }
  }

  const handlePriceChange = (lineId: number, price: number) => {
    const selected = selectedLines.get(lineId)
    if (selected) {
      const validPrice = Math.min(Math.max(0, price), netUnitPrice(selected.line))
      setSelectedLines(new Map(selectedLines.set(lineId, { ...selected, unitPrice: validPrice })))
    }
  }

  // Submit handler
  const handleSubmit = async () => {
    if (!referenceCode || selectedLines.size === 0 || !bill) return

    const lines: CreditNoteLineRequest[] = []
    selectedLines.forEach((selected) => {
      lines.push({
        original_line_id: selected.lineId,
        quantity: selected.quantity,
        unit_price: selected.unitPrice,
      })
    })

    try {
      const result = await createCreditNoteMutation.mutateAsync({
        billId: bill.id,
        data: {
          reference_code: referenceCode,
          reason: reason || undefined,
          lines,
        },
      })

      if (result.success) {
        toast({ title: t('invoicing.creditNoteSuccess') })
        // Navigate to the newly created credit note, or fallback to original bill
        const creditNoteId = result.note_id || result.credit_note_id || null
        navigate(`/bills/${creditNoteId || bill.id}`)
      } else {
        toast({
          title: t('common.error'),
          description: result.message || t('invoicing.creditNoteError'),
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('invoicing.creditNoteError'),
        variant: 'destructive',
      })
    }
  }

  // Can proceed to next step?
  const canProceed = () => {
    if (step === 1) return !!referenceCode
    if (step === 2) return selectedLines.size > 0
    return true
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!bill) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{t('bills.notFound')}</p>
        <Button variant="outline" onClick={() => navigate('/bills')} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/bills/${bill.id}`)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Minus className="h-5 w-5 text-red-500" />
            {t('invoicing.creditNoteTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('invoicing.creditNoteDescription')}
          </p>
        </div>
      </div>

      {/* Step Indicator - 2 steps for cancellation, 3 for return */}
      <ol className="flex items-start justify-center gap-2 py-4">
        {(isFullCancellation ? [1, 3] : [1, 2, 3]).map((s, index, arr) => {
          // For cancellation: step 1 = Motivo, step 3 = Confirmar (shown as step 2)
          const displayNumber = isFullCancellation ? index + 1 : s
          const isCompleted = isFullCancellation
            ? (s === 1 && step === 3)
            : step > s
          const isCurrent = step === s
          const label = s === 1 ? t('invoicing.stepReason') : s === 2 ? t('invoicing.stepItems') : t('invoicing.stepConfirm')

          return (
            <li key={s} className="flex items-start gap-2" aria-current={isCurrent ? 'step' : undefined}>
              <div className="flex w-16 flex-col items-center gap-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium tabular-nums ${
                    isCurrent
                      ? 'bg-primary text-primary-foreground'
                      : isCompleted
                      ? 'bg-success text-success-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {isCompleted ? <Check className="h-5 w-5" aria-hidden="true" /> : displayNumber}
                </div>
                <span className={`text-xs ${isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                  {label}
                </span>
              </div>
              {index < arr.length - 1 && <div className="mt-5 w-6 md:w-14 h-0.5 bg-muted" aria-hidden="true" />}
            </li>
          )
        })}
      </ol>

      {/* Step 1: Reference Code */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('invoicing.selectReferenceCode')}</CardTitle>
            <CardDescription>{t('invoicing.referenceCodeDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('invoicing.selectReferenceCode')} *</Label>
              <Select
                value={referenceCode}
                onValueChange={(value) => handleReferenceCodeChange(value as ReferenceCode)}
              >
                <SelectTrigger className="h-12">
                  <SelectValue placeholder={t('invoicing.selectReferenceCode')} />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_NOTE_REFERENCE_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      <span className="font-mono mr-2">{code}</span>
                      {REFERENCE_CODE_LABELS[code]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('invoicing.referenceReason')}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('invoicing.referenceReasonPlaceholder')}
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Original document info */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-1">
              <p className="text-xs text-muted-foreground">{t('invoicing.originalDocument')}</p>
              <p className="font-medium text-lg">{bill.bill_number || `#${bill.id}`}</p>
              <p className="text-muted-foreground">
                Total: {formatCurrency(bill.total)}
              </p>
              {bill.activity_code && (
                <p className="text-xs text-muted-foreground">
                  {t('activities.economicActivity')}: {bill.activity_code}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Items */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('invoicing.selectItems')}</CardTitle>
            <CardDescription>{t('invoicing.selectItemsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selection actions */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleSelectAll()}>
                {t('invoicing.selectAll')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                {t('invoicing.deselectAll')}
              </Button>
              <Badge variant="secondary" className="ml-auto h-8 px-3">
                {selectedLines.size} / {bill.lines.length}
              </Badge>
            </div>

            {/* Line items */}
            <div className="space-y-3">
              {bill.lines.map((line) => {
                const isSelected = selectedLines.has(line.id)
                const selected = selectedLines.get(line.id)

                return (
                  <div
                    key={line.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleToggleLine(line, !!checked)}
                        className="mt-1 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium leading-tight">{line.article_description}</p>
                        <p className="text-xs text-muted-foreground">{line.article_code}</p>
                        <div className="flex items-center justify-between gap-2 mt-2">
                          <p className="text-sm text-muted-foreground">
                            {line.quantity} x {formatCurrency(line.unit_price)}
                          </p>
                          <p className="font-bold">{formatCurrency(line.total)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Editable fields when selected */}
                    {isSelected && selected && (
                      <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs">{t('invoicing.adjustQuantity')}</Label>
                          <Input
                            type="number"
                            min={1}
                            max={line.quantity}
                            value={selected.quantity}
                            onChange={(e) => handleQuantityChange(line.id, parseInt(e.target.value) || 1)}
                            className="h-12 mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('invoicing.adjustPrice')}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={selected.unitPrice}
                            onChange={(e) => handlePriceChange(line.id, parseFloat(e.target.value) || 0)}
                            className="h-12 mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('invoicing.preview')}</CardTitle>
            <CardDescription>{t('invoicing.previewDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected items summary */}
            <div className="space-y-2">
              {Array.from(selectedLines.values()).map((selected) => (
                <div
                  key={selected.lineId}
                  className="p-3 bg-muted/50 rounded-lg"
                >
                  <p className="font-medium leading-tight">{selected.line.article_description}</p>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-sm text-muted-foreground">
                      {selected.quantity} x {formatCurrency(selected.unitPrice)}
                    </p>
                    <p className="font-medium">
                      {formatCurrency(selected.quantity * selected.unitPrice)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('bills.subtotal')}</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>{t('bills.tax')} (13%)</span>
                <span>{formatCurrency(totals.tax)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>{t('invoicing.noteTotal')}</span>
                <span className="text-red-600">-{formatCurrency(totals.total)}</span>
              </div>
            </div>

            {/* Reference info */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('invoicing.stepReason')}:</span>
                <span className="font-medium">{REFERENCE_CODE_LABELS[referenceCode as ReferenceCode]}</span>
              </div>
              {reason && (
                <div className="text-sm">
                  <span className="text-muted-foreground">{t('invoicing.detail')}: </span>
                  <span>{reason}</span>
                </div>
              )}
            </div>

            {/* Warning */}
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('invoicing.creditNoteWarning')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Fixed Footer Actions */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-background border-t z-50 md:relative md:bottom-0 md:border-0 md:p-0 md:bg-transparent md:z-auto">
        <div className="flex gap-2 max-w-screen-lg mx-auto">
          {step > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                // For cancellation, go back from step 3 directly to step 1
                if (step === 3 && isFullCancellation) {
                  setStep(1)
                } else {
                  setStep(step - 1)
                }
              }}
              className="flex-1 h-12"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('common.previous')}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/bills/${bill.id}`)}
              className="flex-1 h-12"
            >
              {t('common.cancel')}
            </Button>
          )}

          {step < 3 ? (
            <Button
              type="button"
              onClick={() => {
                // For cancellation, skip step 2 and go directly to preview
                if (step === 1 && isFullCancellation) {
                  setStep(3)
                } else {
                  setStep(step + 1)
                }
              }}
              disabled={!canProceed()}
              className="flex-1 h-12"
            >
              {t('common.next')}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={createCreditNoteMutation.isPending}
              className="flex-1 h-12 bg-red-600 hover:bg-red-700"
            >
              {createCreditNoteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {createCreditNoteMutation.isPending
                ? t('invoicing.creating')
                : t('invoicing.confirmCreate')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
